import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { LifeBuoy, Plus, ChevronLeft, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

function Bubble({ m }) {
  const admin = m.author === "admin";
  return (
    <div className={`flex ${admin ? "justify-start" : "justify-end"}`} data-testid={`support-bubble-${m.author}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${admin ? "bg-secondary text-foreground rounded-tl-sm" : "bg-primary text-primary-foreground rounded-tr-sm"}`}>
        <div className={`text-[10px] font-semibold mb-0.5 ${admin ? "text-primary" : "text-primary-foreground/80"}`}>
          {admin ? "Support" : "You"} · {fmtTime(m.created_at)}
        </div>
        <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</p>
      </div>
    </div>
  );
}

export default function SupportCenter({ open, onOpenChange, onRead }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("list"); // list | compose | thread
  const [activeId, setActiveId] = useState(null);
  const [compose, setCompose] = useState({ subject: "", message: "" });
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const active = threads.find((t) => t.id === activeId);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/support/mine");
      setThreads(data);
      onRead && onRead();
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) { setView("list"); setActiveId(null); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const send = async () => {
    if (!compose.message.trim()) { toast.error("Please add a message"); return; }
    setBusy(true);
    try {
      await api.post("/support", compose);
      setCompose({ subject: "", message: "" });
      toast.success("Message sent — thanks, I'll take a look!");
      await load();
      setView("list");
    } catch { toast.error("Couldn't send just now — please try again shortly."); }
    finally { setBusy(false); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    setBusy(true);
    try {
      await api.post(`/support/${active.id}/reply`, { body: reply });
      setReply("");
      await load();
    } catch { toast.error("Couldn't send your reply — please try again."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-w-lg" data-testid="support-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            {view !== "list" ? (
              <button onClick={() => { setView("list"); setActiveId(null); }} data-testid="support-back" className="text-muted-foreground hover:text-foreground">
                <ChevronLeft className="w-5 h-5" />
              </button>
            ) : <LifeBuoy className="w-5 h-5 text-primary" />}
            {view === "compose" ? "New message" : view === "thread" ? (active?.subject || "Conversation") : "Support"}
          </DialogTitle>
        </DialogHeader>

        {view === "list" && (
          <div className="py-1">
            <Button onClick={() => setView("compose")} className="w-full gap-2 mb-4" data-testid="support-new-button">
              <Plus className="w-4 h-4" /> New message
            </Button>
            {loading ? (
              <div className="py-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
            ) : threads.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No messages yet. Something not working, or an idea to share? Start a new message.</p>
            ) : (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1" data-testid="support-thread-list">
                {threads.map((t) => {
                  const last = t.messages[t.messages.length - 1];
                  const hasReply = t.messages.some((m) => m.author === "admin");
                  return (
                    <button key={t.id} data-testid="support-thread-item"
                      onClick={() => { setActiveId(t.id); setView("thread"); }}
                      className="w-full text-left rounded-xl border border-border hover:bg-secondary/50 transition-colors p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium truncate">{t.subject || "(no subject)"}</span>
                        <Badge variant={t.status === "open" ? "default" : "secondary"} className="text-[10px] shrink-0">{t.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        {last?.author === "admin" ? "Support: " : ""}{last?.body}
                      </p>
                      {hasReply && <span className="text-[10px] text-primary font-medium mt-1 inline-block">Has a reply from Support</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {view === "compose" && (
          <div className="space-y-4 py-1">
            <p className="text-sm text-muted-foreground">Tell me what happened, or what you'd love to see. I read every message.</p>
            <div className="space-y-1.5">
              <Label>Subject <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })}
                placeholder="e.g. Gear Sync button greyed out" data-testid="support-subject-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Message</Label>
              <Textarea rows={5} value={compose.message} onChange={(e) => setCompose({ ...compose, message: e.target.value })}
                placeholder="Describe the issue or idea…" data-testid="support-message-input" />
            </div>
            <Button onClick={send} disabled={busy} className="w-full gap-2" data-testid="send-support-button">
              {busy ? "Sending…" : "Send message"}
            </Button>
          </div>
        )}

        {view === "thread" && active && (
          <div className="py-1">
            <div className="flex items-center justify-between mb-3">
              <Badge variant={active.status === "open" ? "default" : "secondary"} className="text-[10px]">{active.status}</Badge>
            </div>
            <div className="space-y-2.5 max-h-[45vh] overflow-y-auto pr-1 mb-3" data-testid="support-thread-messages">
              {active.messages.map((m) => <Bubble key={m.id} m={m} />)}
            </div>
            <div className="flex items-end gap-2">
              <Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)}
                placeholder="Write a reply…" data-testid="support-reply-input" className="resize-none" />
              <Button onClick={sendReply} disabled={busy || !reply.trim()} size="icon" data-testid="support-reply-send" className="shrink-0 h-10 w-10">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
